import { useState } from "react";
import { Link, useNavigate, useOutletContext } from "react-router-dom";
import { useParams } from "react-router";
import dayjs from "dayjs";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import AddIcon from "@mui/icons-material/Add";
import RefreshIcon from "@mui/icons-material/Refresh";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EventBusyIcon from "@mui/icons-material/EventBusy";

import Loader from "../../components/Loader";
import useGetPatientAppointments from "../../hooks/useGetPatientAppointments";
import useDeleteAppointmentsMutation from "../../hooks/useDeleteAppointmentsMutation";

const STATUS_CHIP_MAP = {
  UPCOMING: "info",
  COMPLETED: "success",
  CANCELLED: "error",
  REQUESTED: "warning",
};

const PatientAppointments = () => {
  const { _id_ } = useParams();
  const navigate = useNavigate();
  const {
    data: PatientData = [],
    isLoading,
    refetch,
  } = useGetPatientAppointments({
    patientId: _id_,
  });
  const deleteAppointmentsMutation = useDeleteAppointmentsMutation();

  const { setActive, setBreadcrumbTitle, isTrialLocked } = useOutletContext();
  setActive("appointments");
  setBreadcrumbTitle("Appointments");

  const [appointmentIds, setAppointmentIds] = useState([]);

  const handleAppointmentIds = (e, data) => {
    if (e.target.checked) {
      setAppointmentIds([...appointmentIds, data.id]);
    } else {
      setAppointmentIds(appointmentIds.filter((ele) => ele !== data.id));
    }
  };

  const handleDeleteAppointments = () => {
    deleteAppointmentsMutation.mutate(
      { ids: appointmentIds },
      {
        onSuccess: () => {
          setAppointmentIds([]);
        },
      }
    );
  };

  const handleRowClick = (e, appointmentId) => {
    if (e.target.closest('input[type="checkbox"]') || e.target.closest(".MuiCheckbox-root")) {
      return;
    }
    navigate(`/patient/${_id_}/appointment/${appointmentId}`);
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 1,
          mb: 3,
        }}
      >
        <Typography variant="h6">Appointments</Typography>
        <Stack direction="row" spacing={1}>
          {appointmentIds.length > 0 && (
            <Button
              variant="outlined"
              color="error"
              size="small"
              startIcon={<DeleteOutlineIcon />}
              onClick={handleDeleteAppointments}
              disabled={isTrialLocked}
            >
              Delete ({appointmentIds.length})
            </Button>
          )}
          <Button
            variant="outlined"
            size="small"
            startIcon={<RefreshIcon />}
            onClick={() => refetch()}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            component={Link}
            to={`/patient/${_id_}/appointment/create`}
            disabled={isTrialLocked}
          >
            Create
          </Button>
        </Stack>
      </Box>

      {isLoading ? (
        <Loader />
      ) : PatientData.length ? (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: "grey.50" }}>
                <TableCell padding="checkbox" />
                <TableCell sx={{ fontWeight: 600 }}>Clinic</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Time</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Duration</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Notes</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Created</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {PatientData.map((data) => (
                <TableRow
                  key={data.id}
                  hover
                  sx={{ cursor: "pointer" }}
                  onClick={(e) => handleRowClick(e, data.id)}
                >
                  <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      size="small"
                      checked={appointmentIds.includes(data.id)}
                      onChange={(e) => handleAppointmentIds(e, data)}
                    />
                  </TableCell>
                  <TableCell>{data.clinic_dict.name}</TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    {dayjs(data.time + "Z").format("MMM D, YYYY · h:mm A")}
                  </TableCell>
                  <TableCell>{data.duration} min</TableCell>
                  <TableCell>
                    <Chip
                      label={data.status}
                      color={STATUS_CHIP_MAP[data.status] || "default"}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell
                    sx={{
                      maxWidth: 200,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {data.notes || "—"}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    {dayjs(data.created_at + "Z").format("MMM D, YYYY · h:mm A")}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Box sx={{ textAlign: "center", py: 8 }}>
          <EventBusyIcon sx={{ fontSize: 56, color: "text.disabled", mb: 2 }} />
          <Typography color="text.secondary">No appointments found</Typography>
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            component={Link}
            to={`/patient/${_id_}/appointment/create`}
            sx={{ mt: 2 }}
            disabled={isTrialLocked}
          >
            Create Appointment
          </Button>
        </Box>
      )}
    </Paper>
  );
};

export default PatientAppointments;
